import { gallons } from "./accounts";
import { knownName } from "./memberNames";
import type { LatLng, LookCode, Overview, OverviewRoute, OverviewState, OverviewWeek } from "./api";
import { dayTitle } from "./driverRoutes";
import { age } from "./format";
import { placeable } from "./mapView";
import { SERVER_PROBLEM } from "./save";

/** The states of a stop in the order of the legend and of the stacked bar. */
export const STATES: OverviewState[] = ["done", "checked", "skipped", "late", "open"];

export const STATE_TEXT: Record<OverviewState, string> = {
  done: "Done",
  checked: "Measured",
  skipped: "Skipped",
  late: "Late",
  open: "Open",
};

export const STATE_NOTE: Record<OverviewState, string> = {
  done: "Emptied, all or part",
  checked: "Level read, not emptied",
  skipped: "Skipped or no access",
  late: "Open after the planned time",
  open: "No result",
};

/** The short name of a person: the member name, else the part of the email before "@" and "+". */
export function shortName(email: string | null): string {
  if (!email) return "No driver";
  return knownName(email) ?? email.split("@")[0].split("+")[0];
}

const plural = (count: number, one: string, many: string) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

const names = (list: string[]) => (list.length ? `: ${list.join(", ")}` : "");

/** One item of the summary line, as a full sentence (rules O-2 to O-5). */
export function lookText(code: LookCode, count: number, list: string[], rules: Overview["rules"]): string {
  if (code === "quiet_route") {
    const what = count === 1 ? `Truck ${list[0]} has` : `${plural(count, "route", "routes")} have`;
    return `${what} no report for more than ${rules.quiet_h} h${count === 1 ? "" : names(list.map((t) => `truck ${t}`))}.`;
  }
  if (code === "late_stop") {
    const verb = count === 1 ? "is" : "are";
    return `${plural(count, "stop", "stops")} ${verb} late by more than ${rules.late_min} min${names(list.map((t) => `truck ${t}`))}.`;
  }
  if (code === "overflow") return `${plural(count, "overflow call", "overflow calls")} today${names(list)}.`;
  return `${plural(count, "new sensor alert", "new sensor alerts")} in the last ${rules.alert_h} h.`;
}

/** The Routes page of a day, at the list of routes or at the route of one truck. */
export const routesPath = (day: string, truck?: string) => `/routes?day=${day}#${truck ? `route-${truck}` : "routes-of-the-day"}`;

export function lookPath(code: LookCode, day: string, trucks: string[] = []): string {
  if (code === "sensor_alert") return "/issues?view=all";
  if (code === "overflow") return "/review";
  return routesPath(day, trucks.length === 1 ? trucks[0] : undefined);
}

export type Failure = "session" | "role" | "refused" | "server" | "network";

/** Rule U-17: a plain text for each status; only status 0 is a network problem. */
export function failure(status: number): Failure {
  if (status === 401) return "session";
  if (status === 403) return "role";
  if (status >= 500) return "server";
  return status >= 400 ? "refused" : "network";
}

export const FAILURE_TEXT: Record<Exclude<Failure, "session">, string> = {
  role: "Your role cannot see the overview now. Ask an administrator.",
  refused: "The server did not accept the request for the overview. Try again. If it occurs again, ask an administrator.",
  server: SERVER_PROBLEM,
  network: "Cannot reach the server. Check your connection.",
};

export function statusTitle(found: Overview): string {
  return found.looks.length ? "Needs a look" : "All is well";
}

/** The line under "All is well": what the rules checked. */
export function wellText(found: Overview): string {
  if (!found.routes.length) return "No routes today. No overflow calls and no new sensor alerts.";
  const reporting = found.working ? "Each route reports" : "The working day ended";
  return `${reporting}. No late stops, no overflow calls and no new sensor alerts.`;
}

/** "+12% on the same days of last week", or a text when last week had no gallons. */
export function changeText(now: number, before: number, what: string): string {
  if (before <= 0) return now > 0 ? `No gallons on ${what}` : `None on ${what} too`;
  const change = Math.round(((now - before) / before) * 100);
  if (change === 0) return `The same as ${what}`;
  return `${change > 0 ? "+" : "−"}${Math.abs(change)}% on ${what}`;
}

export function shareText(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–";
}

/** The stops with a result: done, measured or skipped. */
export const reported = (states: Record<OverviewState, number>) => states.done + states.checked + states.skipped;

export function routeText(route: OverviewRoute): string {
  return `${reported(route.states)} of ${route.stops}`;
}

/** The time of the last report and its age, for example "10:42 (35 min ago)". */
export function reportText(at: string | null, now: Date, timeZone?: string): string {
  if (!at) return "No report";
  const time = new Date(at).toLocaleTimeString("en-US", { timeZone, hour: "numeric", minute: "2-digit" });
  return `${time} (${age(at, now)})`;
}

export const routeStatusText: Record<OverviewRoute["status"], string> = {
  planned: "Not started",
  started: "On the road",
  done: "Done",
  cancelled: "Cancelled",
};

/** A length for an SVG attribute: NaN, Infinity and a negative value become 0. */
export const svgLength = (value: number) => (Number.isFinite(value) && value > 0 ? value : 0);

/** The width of a progress bar of part over whole, from 0 to width. */
export function progressWidth(part: number, whole: number, width: number): number {
  const top = svgLength(whole);
  return top > 0 ? Math.min(1, svgLength(part) / top) * svgLength(width) : 0;
}

export interface Segment {
  state: OverviewState;
  x: number;
  width: number;
}

/** The parts of a stacked bar of the stop states, in STATES order. */
export function segments(states: Record<OverviewState, number>, width: number): Segment[] {
  const total = STATES.reduce((sum, state) => sum + states[state], 0);
  if (!total) return [];
  let x = 0;
  return STATES.filter((state) => states[state] > 0).map((state) => {
    const part = svgLength((states[state] / total) * width);
    const found = { state, x: Math.round(x * 10) / 10, width: Math.round(part * 10) / 10 };
    x += part;
    return found;
  });
}

export interface Bar {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

/** Bars from the base line up, with a gap between them; a zero has no height. */
export function bars(values: number[], width: number, height: number, gap = 2, top?: number): Bar[] {
  const max = top ?? Math.max(0, ...values);
  const step = values.length ? width / values.length : 0;
  return values.map((value, index) => {
    const h = max > 0 ? svgLength(Math.round((value / max) * height * 10) / 10) : 0;
    return { x: Math.round((index * step + gap / 2) * 10) / 10, y: Math.round((height - h) * 10) / 10, width: Math.max(1, Math.round((step - gap) * 10) / 10), height: h, value };
  });
}

export const weekTop = (weeks: OverviewWeek[]) => Math.max(0, ...weeks.flatMap((week) => [week.gallons, week.before_gal]));

/** A day as "Mon Aug 24". */
export const dateText = (day: string) => dayTitle(day, "");

/** Whole gallons with the unit, for example "1,650 gal". */
export const galWhole = (value: number) => `${gallons(Math.round(value))} gal`;

/** The gallons of the stops of the routes of today, against their plan. */
export function stopsGalText(collected: number, planned: number): string {
  if (planned <= 0) return `Route stops: ${galWhole(collected)}, no planned gallons`;
  return `Route stops: ${gallons(Math.round(collected))} of ${galWhole(planned)} planned (${shareText(collected, planned)})`;
}

export const ROUTE_GAL_UNKNOWN = "Route gallons are not known yet";

/** The route stop text, or a plain note when the API does not send the route gallons. */
export const routeGalText = (collected: number | undefined, planned: number) => (collected === undefined ? ROUTE_GAL_UNKNOWN : stopsGalText(collected, planned));

export const allPickupsText = (value: number) => `All pickups today: ${galWhole(value)}`;

export function weeksLabel(weeks: OverviewWeek[]): string {
  return weeks.map((week) => `week of ${dateText(week.week_start)}: ${galWhole(week.gallons)}, the same week 8 weeks before ${galWhole(week.before_gal)}`).join("; ");
}

export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The day names of the week from its first day, for example Sun to Sat. */
export function dayNames(weekStart: string): string[] {
  const first = (new Date(`${weekStart}T12:00:00Z`).getUTCDay() + 6) % 7;
  return Array.from({ length: 7 }, (_, n) => DAY_NAMES[(first + n) % 7]);
}

export const galShort = (value: number) => (value >= 10_000 ? `${gallons(Math.round(value / 100) / 10)}k` : gallons(Math.round(value)));

export const AGE_TEXT = ["Under 1 day", "1 to 7 days", "7 to 30 days", "Over 30 days"];

/** The milliseconds since a start, as a whole number. */
export const since = (start: number) => Math.max(0, Math.round(performance.now() - start));

/** The points of today that the map draws over the account pins, without the points that a map cannot show. */
export function todayPoints(found: Overview): { points: LatLng[]; accounts: string[] } {
  const points = [...found.points, ...found.routes.flatMap((route) => (route.last_stop ? [route.last_stop] : [])), ...found.tanks.points];
  const accounts = [...found.points, ...found.tanks.points].map((point) => point.account_id);
  return { points: points.filter(placeable).map(({ lat, lng }) => ({ lat, lng })), accounts };
}
